
import type { PropOptions } from 'vue';
import Vue from 'vue';
import { mapActions } from 'vuex';
import type { PublicPortfolioConfiguration, PublicPortfolioCurrentUser, Testimonial } from '@/types/graphql';
import PublicPortfolioModal from '~/components/partials/public-portfolio/PublicPortfolioModal.vue';
import BravadoCallout from '~/components/common/BravadoCallout/BravadoCallout.vue';
import BravadoToggle from '~/components/common/BravadoToggle/BravadoToggle.vue';
import BravadoButtonV3 from '~/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import BravadoText from '~/components/common/BravadoText/BravadoText.vue';
import VerifiedTestimonial from '~/components/partials/public-portfolio/PublicPortfolioTestimonials/VerifiedTestimonial.vue';
import ActionNeededTestimonial from '~/components/partials/public-portfolio/PublicPortfolioTestimonials/ActionNeededTestimonial.vue';
import PendingTestimonial from '~/components/partials/public-portfolio/PublicPortfolioTestimonials/PendingTestimonial.vue';
import ArchivedTestimonial from '~/components/partials/public-portfolio/PublicPortfolioTestimonials/ArchivedTestimonial.vue';
import {
  ARCHIVING_TESTIMONIAL_STATE,
  PENDING_STATES_MAP,
  TAB_KEYS,
} from '~/components/partials/public-portfolio/PublicPortfolioTestimonials/constants';
import ArchiveTestimonialForPublicPortfolioCurrentUser from '~/apollo/mutations/Portfolio/ArchiveTestimonialForPublicPortfolioCurrentUser.mutations';
import UndoTestimonialForPublicPortfolioCurrentUser from '~/apollo/mutations/Portfolio/UndoTestimonialForPublicPortfolioCurrentUser.mutations';
import DestroyTestimonialForPublicPortfolioCurrentUser from '~/apollo/mutations/Portfolio/DestroyTestimonialForPublicPortfolioCurrentUser.mutations';
import ReRequestTestimonialForPublicPortfolioCurrentUser from '~/apollo/mutations/Portfolio/ReRequestTestimonialForPublicPortfolioCurrentUser.mutations';
import { PublicPortfolioCurrentUserQuery } from '~/apollo/queries/PublicPortfolio/PublicPortfolioCurrentUser.query';
import UpdateTestimonialPositionForPublicPortfolioCurrentUser from '~/apollo/mutations/Portfolio/UpdateTestimonialPositionForPublicPortfolioCurrentUser.mutations';
import { TestimonialState } from '~/types/graphql';
import { updateTestimonial } from '~/components/partials/public-portfolio/PublicPortfolioTestimonials/utils';
import type { PublicPortfolioTestimonialPropMixinType } from '~/components/partials/public-portfolio/PublicPortfolioTestimonials/mixins/publicPortfolioTestimonialPropMixin';
import publicPortfolioTestimonialPropMixin from '~/components/partials/public-portfolio/PublicPortfolioTestimonials/mixins/publicPortfolioTestimonialPropMixin';
import EmptyTestimonialsPlaceholder from '~/components/partials/public-portfolio/PublicPortfolioTestimonials/EmptyTestimonialsPlaceholder.vue';
import { groupBy } from '~/components/utils/core/collection';

export default (Vue as PublicPortfolioTestimonialPropMixinType).extend({
  name: 'PublicPortfolioEditTestimonialsModal',
  components: {
    EmptyTestimonialsPlaceholder,
    BravadoDraggableItems: () => import('~/components/common/BravadoDraggableItems/BravadoDraggableItems.vue'),
    ArchivedTestimonial,
    PendingTestimonial,
    ActionNeededTestimonial,
    VerifiedTestimonial,
    BravadoText,
    BravadoButtonV3,
    PublicPortfolioModal,
    BravadoCallout,
    BravadoToggle,
  },
  mixins: [publicPortfolioTestimonialPropMixin],
  props: {
    configuration: {
      type: Object,
      required: true,
    } as PropOptions<PublicPortfolioConfiguration>,
  },
  data() {
    return {
      tabKeys: TAB_KEYS,
      tabs: Object.freeze([
        { key: TAB_KEYS.verified, title: 'Verified' },
        { key: TAB_KEYS.actionNeeded, title: 'Action needed' },
        { key: TAB_KEYS.pending, title: 'Pending' },
        { key: TAB_KEYS.archived, title: 'Archived' },
      ]),
      selectedTabKey: 'verified',
    };
  },
  computed: {
    orderedPendingTestimonials(): Array<Testimonial> {
      const testimonialByState = groupBy(
        this.pendingTestimonials,
        (testimonial: Testimonial) => PENDING_STATES_MAP[testimonial.state],
      );
      return [...(testimonialByState.verifying || []), ...(testimonialByState.requested || [])];
    },
  },
  methods: {
    ...mapActions('portfolio', ['updatePortfolioConfig']),
    getPendingTestimonialState(state: string) {
      return PENDING_STATES_MAP[state];
    },
    onBeforeOpen(): void {
      const tab = (this.$route.query.tab as string) || '';
      this.selectedTabKey = Object.values(TAB_KEYS).includes(tab) ? tab : 'verified';
    },
    async reorderTestimonial({ element, newPosition }: { element: Testimonial; newPosition: number }): Promise<void> {
      try {
        await this.$apollo.mutate({
          mutation: UpdateTestimonialPositionForPublicPortfolioCurrentUser,
          variables: {
            input: { id: element.id, newPosition },
          },
        });
        this.$notice.success('Testimonials reordered!');
      } catch (e) {
        this.$notice.error(e);
        this.$sentryUtils.error(e);
      }
    },
    onVisibilityChange(value: boolean): void {
      this.$emit('upsert', {
        configurationAttrs: {
          sectionTestimonialsIsPublic: value,
        },
      });
    },
    async archiveTestimonial(id: string, prevState: string = ''): Promise<void> {
      try {
        await this.$apollo.mutate({
          mutation: ArchiveTestimonialForPublicPortfolioCurrentUser,
          variables: {
            input: { id },
          },
          update(
            store,
            {
              data: {
                archiveTestimonialForPublicPortfolioCurrentUser: { state },
              },
            },
          ) {
            updateTestimonial(store, id, {
              state: prevState === TestimonialState.Verified ? ARCHIVING_TESTIMONIAL_STATE : state,
            });
          },
        });
        this.$notice.success('Testimonial archived successfully');
      } catch (e) {
        this.$notice.error(e);
        this.$sentryUtils.error(e);
      }
    },
    async restoreTestimonial(id: string): Promise<void> {
      try {
        await this.$apollo.mutate({
          mutation: UndoTestimonialForPublicPortfolioCurrentUser,
          variables: {
            input: { id },
          },
          update(
            store,
            {
              data: {
                undoTestimonialForPublicPortfolioCurrentUser: { state },
              },
            },
          ) {
            updateTestimonial(store, id, { state });
          },
        });

        this.$notice.success('Testimonial restored successfully');
      } catch (e) {
        this.$notice.error(e);
        this.$sentryUtils.error(e);
      }
    },
    async destroyTestimonial(id: string): Promise<void> {
      if (!confirm('Are you sure you want to remove this testimonial?')) {
        return;
      }
      try {
        await this.$apollo.mutate({
          mutation: DestroyTestimonialForPublicPortfolioCurrentUser,
          variables: {
            input: { id },
          },
          update: store => {
            const query = {
              query: PublicPortfolioCurrentUserQuery,
            };
            const data = store.readQuery<{ publicPortfolioCurrentUser: PublicPortfolioCurrentUser }>(query);
            if (!data) return;
            const testimonials = data.publicPortfolioCurrentUser.testimonials || [];
            data.publicPortfolioCurrentUser.testimonials = testimonials.filter(t => t.id !== id);

            store.writeQuery({ ...query, data });
          },
        });
        this.$notice.success('Testimonial removed successfully');
      } catch (e) {
        this.$notice.error(e);
        this.$sentryUtils.error(e);
      }
    },
    async resendTestimonial(id: string): Promise<void> {
      try {
        await this.$apollo.mutate({
          mutation: ReRequestTestimonialForPublicPortfolioCurrentUser,
          variables: {
            input: { id },
          },
          update(
            store,
            {
              data: {
                reRequestTestimonialForPublicPortfolioCurrentUser: { state },
              },
            },
          ) {
            updateTestimonial(store, id, { state });
          },
        });
        this.$notice.success('Testimonial requested successfully');
      } catch (e) {
        this.$notice.error(e);
        this.$sentryUtils.error(e);
      }
    },
  },
});
